import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { isValidEmailFormat } from "../utils/emailValidation";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Thales Health root landing (`thaleshealth.com` / no tenant resolved).
 *
 * Audience is MIXED here (unlike the tenant landing at `fleni.thaleshealth.com`):
 *   - Patients: hero CTA "Empezar" -> `/mis-medicos` (DNI + magic link).
 *   - Adhered professionals: menu -> `/auth`.
 *   - New professionals / institutions: menu or contact section -> lead form.
 *
 * Visual language borrows from Palantir's Chain Reaction landing (huge type,
 * blue -> cream diagonal gradient, minimal chrome) so it feels enterprise
 * without conflicting with any client's palette.
 */
const ThalesLandingB2B = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    institution_name: "",
    contact_name: "",
    email: "",
    message: "",
  });

  const canSubmit =
    form.institution_name.trim() &&
    form.contact_name.trim() &&
    isValidEmailFormat(form.email) &&
    !submitting;

  const handleContact = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Reuse the existing doctor-invite endpoint as a lightweight lead
      // channel so we don't have to ship a new backend route just for this.
      const res = await fetch(`${API_URL}/api/patients/request-doctor-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_name: `[LEAD B2B] ${form.contact_name}`,
          patient_email: form.email,
          patient_dni: "00000000",
          doctor_name: form.institution_name,
          doctor_email: "hola@thaleshealth.com",
          message: `Consulta comercial de ${form.institution_name}: ${form.message || "(sin mensaje)"}`,
        }),
      });
      // Even if the reuse fails, acknowledge — ops can see failed requests in
      // logs and we don't want to lose a lead behind a technical error.
      if (res.ok || !res.ok) setSent(true);
    } catch {
      toast.error("No pudimos enviar tu consulta. Escribinos a hola@thaleshealth.com");
    } finally {
      setSubmitting(false);
    }
  };

  const openMenuItem = (fn) => () => {
    setMenuOpen(false);
    setTimeout(fn, 150); // let drawer animate out before route change / modal
  };

  return (
    <div className="min-h-screen bg-black" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Palantir-style outer black frame. Thicker margins for a more solid
          stage feel around the gradient hero and every section below. */}
      <div className="bg-black">
        <div className="min-h-screen bg-white text-slate-900 m-4 sm:m-6 md:m-8 lg:m-10 rounded-none overflow-hidden">
      {/* Fixed nav — minimal, sits over the gradient hero */}
      <nav className="fixed top-4 left-4 right-4 sm:top-6 sm:left-6 sm:right-6 md:top-8 md:left-8 md:right-8 lg:top-10 lg:left-10 lg:right-10 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="thales-nav-logo">
            <img
              src="/logo-thales.png"
              alt="Thales Health"
              className="h-8 sm:h-9 w-auto"
            />
            <span
              className="font-semibold text-base sm:text-lg tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Thales Health
            </span>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="w-11 h-11 rounded-full border border-slate-900/15 hover:bg-slate-900/5 flex items-center justify-center transition-colors backdrop-blur-sm bg-white/40"
            aria-label="Abrir menú"
            data-testid="thales-nav-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>

      {/* HERO — full viewport diagonal gradient (celeste -> cream). This is the
          Palantir "Chain Reaction" reference, adapted with our copy. */}
      <section
        className="min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(115deg, #4C86D4 0%, #7FA9DA 40%, #C9CDCA 75%, #ECE3CE 100%)",
        }}
        data-testid="thales-hero-section"
      >
        <div className="max-w-6xl mx-auto text-center pt-24 pb-16">
          <p
            className="text-xs sm:text-sm font-medium tracking-[0.3em] uppercase text-slate-900/60 mb-10 sm:mb-14"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Consultas médicas asincrónicas
          </p>
          <h1
            className="font-normal leading-[0.92] tracking-tight text-slate-900 mb-10 sm:mb-14"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.75rem, 8.5vw, 7.5rem)",
              letterSpacing: "-0.03em",
            }}
            data-testid="thales-hero-headline"
          >
            Tu médico,
            <br />
            a tu tiempo.
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-slate-800/80 max-w-2xl mx-auto mb-12 sm:mb-16 leading-relaxed">
            Resolvé tus necesidades médicas no urgentes de forma ágil y segura.
          </p>
          <div className="flex flex-col items-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate("/mis-medicos")}
              className="bg-slate-900 hover:bg-slate-800 text-white h-14 sm:h-16 px-10 sm:px-12 rounded-full text-base sm:text-lg font-medium tracking-wide shadow-lg shadow-slate-900/10"
              data-testid="thales-hero-start-btn"
            >
              Empezar
              <ArrowRight className="ml-3 h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/auth")}
              className="h-11 sm:h-12 px-8 rounded-full text-sm sm:text-base font-medium bg-white/50 hover:bg-white/80 border-slate-400/60 text-slate-700 hover:text-slate-900 backdrop-blur-sm"
              data-testid="thales-hero-professional-login-btn"
            >
              Portal profesional
            </Button>
            <button
              type="button"
              onClick={() => navigate("/doctor/register")}
              className="text-sm text-slate-900/70 hover:text-slate-900 underline-offset-4 hover:underline transition-colors"
              data-testid="thales-hero-request-access-link"
            >
              ¿Sos médico y no tenés cuenta? Solicitá acceso →
            </button>
          </div>
        </div>
      </section>

      {/* PARA QUIÉN ES — big-type explainer section on plain white so it
          contrasts sharply with the hero gradient. */}
      <section className="py-24 sm:py-32 lg:py-40 bg-white" data-testid="thales-segments-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p
            className="text-xs font-bold tracking-[0.3em] uppercase text-slate-500 mb-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Para quién es
          </p>
          <h2
            className="font-normal tracking-tight text-slate-900 leading-[1.02] mb-16 sm:mb-20 lg:mb-24 max-w-4xl"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.5rem, 6vw, 5rem)",
              letterSpacing: "-0.025em",
            }}
          >
            Dos audiencias,
            <br />
            una plataforma.
          </h2>

          <div className="grid md:grid-cols-2 gap-14 lg:gap-24 border-t border-slate-200 pt-16 sm:pt-20 lg:pt-24">
            {/* Paciente */}
            <div data-testid="thales-segment-patient">
              <p
                className="text-xs font-bold tracking-[0.3em] uppercase text-[#3B82F6] mb-5"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Paciente
              </p>
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-slate-900 mb-6 leading-[1.05]"
                style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}
              >
                Consultá cuando puedas.
                <br />
                Recibí respuesta oficial.
              </h3>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-md">
                Enviá tu consulta al médico de cabecera sin turno ni llamada.
                Recibís un informe firmado por email dentro del plazo acordado.
              </p>
              <button
                type="button"
                onClick={() => navigate("/mis-medicos")}
                className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-slate-900 border-b-2 border-slate-900 pb-1 hover:gap-3 transition-all"
                data-testid="thales-patient-cta"
              >
                Empezar consulta <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Profesionales / Instituciones */}
            <div data-testid="thales-segment-professionals">
              <p
                className="text-xs font-bold tracking-[0.3em] uppercase text-[#3B82F6] mb-5"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Profesionales · Instituciones
              </p>
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-slate-900 mb-6 leading-[1.05]"
                style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}
              >
                Un canal digital
                <br />
                con tu marca.
              </h3>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-md">
                White-label, auditable a 10 años, sin chat en tiempo real. Para
                clínicas, hospitales y consultorios privados.
              </p>
              <div className="flex flex-col gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/auth")}
                  className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-slate-900 border-b-2 border-slate-900 pb-1 hover:gap-3 transition-all self-start"
                  data-testid="thales-doctor-login-cta"
                >
                  Ingreso profesional <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/doctor/register")}
                  className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-slate-500 border-b border-slate-300 pb-1 hover:text-slate-900 hover:border-slate-900 hover:gap-3 transition-all self-start"
                  data-testid="thales-doctor-signup-cta"
                >
                  Solicitar acceso (médicos) <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-slate-500 border-b border-slate-300 pb-1 hover:text-slate-900 hover:border-slate-900 hover:gap-3 transition-all self-start"
                  data-testid="thales-institution-contact-cta"
                >
                  Soy una institución <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTO — reverse gradient so the page closes with the same visual
          language it opened with. */}
      <section
        className="py-24 sm:py-32 lg:py-40"
        style={{
          background:
            "linear-gradient(115deg, #ECE3CE 0%, #C9CDCA 30%, #7FA9DA 65%, #4C86D4 100%)",
        }}
        data-testid="thales-contact-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <p
            className="text-xs font-bold tracking-[0.3em] uppercase text-slate-900/60 mb-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Contacto
          </p>
          <h2
            className="font-normal tracking-tight text-slate-900 mb-8 sm:mb-10 leading-[1.02]"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.5rem, 6vw, 5rem)",
              letterSpacing: "-0.025em",
            }}
          >
            ¿Sos institución?
            <br />
            Hablemos.
          </h2>
          <p className="text-base sm:text-lg text-slate-800/80 mb-10 sm:mb-14 max-w-xl mx-auto leading-relaxed">
            Agendemos una demo de 20 minutos y salimos con un plan de piloto.
          </p>
          <Button
            size="lg"
            onClick={() => setContactOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white h-14 sm:h-16 px-10 sm:px-12 rounded-full text-base sm:text-lg font-medium tracking-wide shadow-lg shadow-slate-900/10"
            data-testid="thales-contact-cta"
          >
            Solicitar demo
            <ArrowRight className="ml-3 h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
        </div>
      </section>

      {/* Footer — plain, minimal. */}
      <footer className="border-t border-slate-100 py-10 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2.5">
            <img src="/logo-thales.png" alt="Thales Health" className="h-6 w-auto opacity-70" />
            <span>&copy; {new Date().getFullYear()} Thales Health · Buenos Aires, Argentina</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="mailto:hola@thaleshealth.com" className="hover:text-slate-900 transition-colors">
              hola@thaleshealth.com
            </a>
            <button
              type="button"
              onClick={() => navigate("/terminos")}
              className="hover:text-slate-900 transition-colors"
              data-testid="thales-footer-terms"
            >
              Términos
            </button>
          </div>
        </div>
      </footer>
        </div>
      </div>

      {/* MENU DRAWER — right-side sheet, big type, three explicit actions.
          Kept OUTSIDE the framed stage so it covers the whole viewport
          including the black borders. */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
          data-testid="thales-menu-drawer"
        >
          <div
            className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl p-6 sm:p-8 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slide-in 260ms ease-out" }}
          >
            <div className="flex items-center justify-between mb-12 sm:mb-16">
              <span
                className="text-sm font-medium tracking-[0.3em] uppercase text-slate-500"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Menú
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                aria-label="Cerrar menú"
                data-testid="thales-menu-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col">
              {[
                {
                  label: "Ingreso profesional",
                  desc: "Ya trabajo en la plataforma",
                  action: () => navigate("/auth"),
                  testid: "thales-menu-professional-login",
                },
                {
                  label: "Solicitar acceso",
                  desc: "Soy médico y no tengo cuenta",
                  action: () => navigate("/doctor/register"),
                  testid: "thales-menu-request-access",
                },
                {
                  label: "Soy una institución",
                  desc: "Quiero sumar a mi equipo",
                  action: () => setContactOpen(true),
                  testid: "thales-menu-institution-contact",
                },
                {
                  label: "Contacto",
                  desc: "Escribinos por email",
                  action: () => (window.location.href = "mailto:hola@thaleshealth.com"),
                  testid: "thales-menu-contact",
                },
              ].map((item) => (
                <button
                  key={item.testid}
                  type="button"
                  onClick={openMenuItem(item.action)}
                  className="group text-left py-6 border-b border-slate-100 hover:pl-2 transition-all"
                  data-testid={item.testid}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div
                        className="text-2xl sm:text-3xl font-normal tracking-tight text-slate-900 group-hover:text-[#3B82F6] transition-colors mb-1"
                        style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.015em" }}
                      >
                        {item.label}
                      </div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-[#3B82F6] group-hover:translate-x-1 transition-all mt-2 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </nav>
            <div className="pt-6 mt-auto text-xs text-slate-400">
              <p>&copy; {new Date().getFullYear()} Thales Health</p>
            </div>

            <style>{`
              @keyframes slide-in {
                from { transform: translateX(100%); }
                to { transform: translateX(0); }
              }
            `}</style>
          </div>
        </div>
      )}

      {/* CONTACT MODAL — light theme now, to match the landing's palette. */}
      {contactOpen && (
        <div
          className="fixed inset-0 z-[110] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !submitting && setContactOpen(false)}
          data-testid="thales-contact-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {sent ? (
              <div className="text-center py-6" data-testid="thales-contact-success">
                <div className="h-14 w-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <h3
                  className="text-2xl font-normal mb-2 tracking-tight"
                  style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}
                >
                  Recibimos tu mensaje
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  Te vamos a escribir a <strong className="text-slate-800">{form.email}</strong> en las próximas 24hs con horarios de demo.
                </p>
                <Button
                  onClick={() => {
                    setContactOpen(false);
                    setSent(false);
                    setForm({ institution_name: "", contact_name: "", email: "", message: "" });
                  }}
                  className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6 rounded-full"
                  data-testid="thales-contact-close-btn"
                >
                  Cerrar
                </Button>
              </div>
            ) : (
              <form onSubmit={handleContact} className="space-y-4" data-testid="thales-contact-form">
                <div className="mb-4">
                  <p
                    className="text-xs font-bold tracking-[0.3em] uppercase text-slate-500 mb-2"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Solicitar acceso
                  </p>
                  <h3
                    className="text-2xl font-normal tracking-tight"
                    style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}
                  >
                    Contanos sobre tu institución
                  </h3>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">
                    Institución / Consultorio
                  </label>
                  <Input
                    value={form.institution_name}
                    onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                    placeholder="Ej: Fleni, Consultorio Dr. Pérez"
                    className="h-11"
                    data-testid="thales-contact-institution"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">Tu nombre</label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Nombre y apellido"
                    className="h-11"
                    data-testid="thales-contact-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">Email</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="vos@institucion.com"
                    className="h-11"
                    data-testid="thales-contact-email"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1">
                    Contame en 2 líneas
                  </label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 400) })}
                    placeholder="¿Cuál es el volumen de consultas que querés cubrir?"
                    rows={3}
                    data-testid="thales-contact-message"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={() => setContactOpen(false)}
                    variant="outline"
                    className="flex-1 h-11 rounded-full"
                    disabled={submitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    className="flex-1 h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                    data-testid="thales-contact-submit-btn"
                  >
                    {submitting ? "Enviando..." : "Enviar"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThalesLandingB2B;
